import type { PropType } from 'vue';
import {
    UPDATE_MODEL_VALUE,
    type ComponentSizes, componentSizeProp,
    type ComponentView, componentViewProp,
    type ClearableType, clearableProp,
} from '../../constants';

export type PickerValue = string | {
    year?: string;
    month?: string;
    day?: string;
    hours?: string;
    minutes?: string;
    seconds?: string;
};

export type PickerType =
    | 'date'
    | 'date-range'
    | 'date-range-plus'
    | 'date-time'
    | 'month'
    | 'month-range';

/** Конкретная сторона — жёсткая, 'auto' — подбор под свободное место на экране. */
export type PickerPosition = 'top' | 'right' | 'bottom' | 'left' | 'auto';

export type RangeDirection = '--to-left' | '--to-right' | null;

export interface GroupingOption {
    value: string;
    label: string;
}

export interface DayCell {
    key: string;
    day: number;
    date: Date;
    isOtherMonth: boolean;
    isToday: boolean;
    isSelected: boolean;
    isDisabled: boolean;
    isRangeStart: boolean;
    isRangeEnd: boolean;
    isInRange: boolean;
}

export interface MonthCell {
    idx: number;
    label: string;
    isSelected: boolean;
    isRangeStart: boolean;
    isRangeEnd: boolean;
    isInRange: boolean;
}

export const MONTHS_SHORT = [
    'янв.', 'февр.', 'март', 'апр.', 'май', 'июнь',
    'июль', 'авг.', 'сент.', 'окт.', 'нояб.', 'дек.',
];

export type AvailableDates = {
    start?: Date | string | null;
    end?: Date | string | null;
};

export interface DatepickerProps {
    modelValue?: PickerValue | null;
    type?: PickerType;
    format?: string;
    placeholder?: string;
    label?: string;
    disabled?: boolean;
    readonly?: boolean;
    clearable?: ClearableType;
    size?: ComponentSizes;
    view?: ComponentView;
    position?: PickerPosition;
    availableDates?: AvailableDates;
    returnObject?: boolean;
    onlyHours?: boolean;
    grouping?: GroupingOption[];
    groupingValue?: string;
}

export const datepickerProps = {
    modelValue: {
        type: [String, Object, Array] as PropType<PickerValue | PickerValue[] | null>,
        default: undefined,
        required: false,
    },
    type: {
        type: String as PropType<PickerType>,
        default: 'date' as PickerType,
        required: false,
    },
    format: {
        type: String as PropType<string>,
        default: 'YYYY-MM-DD',
        required: false,
    },
    placeholder: {
        type: String as PropType<string>,
        default: undefined,
        required: false,
    },
    label: {
        type: String as PropType<string>,
        default: undefined,
        required: false,
    },
    disabled: {
        type: Boolean as PropType<boolean>,
        default: undefined,
        required: false,
    },
    readonly: {
        type: Boolean as PropType<boolean>,
        default: undefined,
        required: false,
    },
    clearable: clearableProp,
    size: componentSizeProp,
    view: componentViewProp,
    position: {
        type: String as PropType<PickerPosition>,
        // Календарь высокий и у нижнего края экрана обязан переезжать наверх,
        // поэтому по умолчанию сторона подбирается, а не фиксируется.
        default: 'auto' as PickerPosition,
        required: false,
    },
    availableDates: {
        type: Object as PropType<AvailableDates>,
        default: undefined,
        required: false,
    },
    returnObject: {
        type: Boolean as PropType<boolean>,
        default: false,
        required: false,
    },
    onlyHours: {
        type: Boolean as PropType<boolean>,
        default: false,
        required: false,
    },
    grouping: {
        type: Array as PropType<GroupingOption[]>,
        default: undefined,
        required: false,
    },
    groupingValue: {
        type: String as PropType<string>,
        default: undefined,
        required: false,
    },
};

export const datepickerEmits = {
    [UPDATE_MODEL_VALUE]: (_value: string | PickerValue | PickerValue[] | string[] | null) => true,
    'update:groupingValue': (_value: string) => true,
};
